import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton, Typography as MuiTypography, Tooltip } from '@mui/material';
import { Tag, Switch, Input, Space } from 'antd';
import {
  GroupOutlined as GroupIcon,
  DeleteOutlined as DeleteIcon,
  Instagram as InstagramIcon,
  SearchOutlined as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import moment from 'moment';

import {
  fetchCommunityMembers,
  updateCommunityMemberAsync,
  deleteCommunityMemberAsync,
} from '../redux/communitySlice';
import DataTable from '../components/common/DataTable';
import PageHeader from '../components/common/PageHeader';
import DeleteConfirmDialog from '../components/common/DeleteConfirmDialog';

const CommunityPage = () => {
  const dispatch = useDispatch();
  const { members, status, actionLoading } = useSelector((state) => state.community);

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  useEffect(() => {
    dispatch(fetchCommunityMembers());
  }, [dispatch]);

  const handleToggleStatus = async (row) => {
    try {
      const resultAction = await dispatch(
        updateCommunityMemberAsync({
          id: row.id,
          data: { isActive: !row.isActive },
        })
      );
      if (updateCommunityMemberAsync.fulfilled.match(resultAction)) {
        toast.success(`Member status marked as ${!row.isActive ? 'Active' : 'Inactive'}`);
      } else {
        toast.error(resultAction.payload || 'Failed to update status');
      }
    } catch (err) {
      toast.error('An unexpected error occurred');
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedMemberId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedMemberId) return;
    setDeleteLoading(true);
    try {
      const resultAction = await dispatch(deleteCommunityMemberAsync(selectedMemberId));
      if (deleteCommunityMemberAsync.fulfilled.match(resultAction)) {
        toast.success('Community member deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedMemberId(null);
      } else {
        toast.error(resultAction.payload || 'Failed to delete member');
      }
    } catch (err) {
      toast.error('An unexpected error occurred');
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredMembers = members.filter((m) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const name = (m.name || '').toLowerCase();
    const email = (m.email || '').toLowerCase();
    const insta = (m.instagram || '').toLowerCase();
    return name.includes(q) || email.includes(q) || insta.includes(q);
  });

  const columns = [
    {
      id: 'name',
      label: 'NAME',
      width: '220px',
      render: (row) => (
        <MuiTypography sx={{ fontWeight: 600, color: 'var(--header-title)' }}>
          {row.name}
        </MuiTypography>
      ),
    },
    {
      id: 'email',
      label: 'EMAIL ADDRESS',
      width: '240px',
      render: (row) => (
        <a
          href={`mailto:${row.email}`}
          style={{ color: '#2563eb', textDecoration: 'none', fontSize: '0.9rem' }}
        >
          {row.email}
        </a>
      ),
    },
    {
      id: 'instagram',
      label: 'INSTAGRAM',
      width: '200px',
      render: (row) => {
        if (!row.instagram) {
          return (
            <MuiTypography variant="caption" sx={{ color: '#94a3b8' }}>
              None
            </MuiTypography>
          );
        }
        const handle = row.instagram.replace(/^@/, '');
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <InstagramIcon sx={{ fontSize: 16, color: '#e1306c' }} />
            <a
              href={`https://instagram.com/${handle}`}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--header-title)', textDecoration: 'none', fontSize: '0.9rem' }}
            >
              @{handle}
            </a>
          </Box>
        );
      },
    },
    {
      id: 'isActive',
      label: 'STATUS',
      width: '140px',
      align: 'center',
      render: (row) => (
        <Space size="middle">
          <Switch
            checked={Boolean(row.isActive)}
            loading={actionLoading}
            onChange={() => handleToggleStatus(row)}
            size="small"
          />
          <Tag color={row.isActive ? 'green' : 'default'}>
            {row.isActive ? 'ACTIVE' : 'INACTIVE'}
          </Tag>
        </Space>
      ),
    },
    {
      id: 'createdAt',
      label: 'JOINED ON',
      width: '160px',
      render: (row) => (
        <MuiTypography variant="caption" sx={{ color: 'var(--subtitle-text)', fontWeight: 500 }}>
          {moment(row.createdAt).format('MMM DD, YYYY')}
        </MuiTypography>
      ),
    },
    {
      id: 'actions',
      label: 'ACTIONS',
      align: 'right',
      width: '100px',
      render: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="Delete Member">
            <IconButton
              onClick={() => handleDeleteClick(row.id)}
              size="small"
              sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}
            >
              <DeleteIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box className="premium-container">
      <PageHeader
        title="Community Members"
        subtitle="Manage subscribed community members, view contact handles, and toggle active status."
        titleIcon={<GroupIcon />}
      />

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Input
          placeholder="Search by name, email, or @instagram..."
          prefix={<SearchIcon sx={{ color: '#94a3b8', fontSize: 20, mr: 0.5 }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          style={{ maxWidth: 360, borderRadius: 8, height: 40 }}
        />
        <MuiTypography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
          Total Members: <strong style={{ color: '#0f172a' }}>{members.length}</strong>
        </MuiTypography>
      </Box>

      <Box className="premium-table-card">
        <DataTable
          premium={true}
          columns={columns}
          data={filteredMembers}
          loading={status === 'loading'}
          emptyTitle="No Community Members Found"
          emptySubtitle="No community members have joined yet or match your search."
        />
      </Box>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName="community member"
        loading={deleteLoading}
      />
    </Box>
  );
};

export default CommunityPage;
